import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Star as StarMiniIcon } from '@mui/icons-material';
import { Rating } from '../types/rating';
import { ratingService } from '../services/ratingService';
import RatingComponent from '../components/RatingComponent';
import orderService from '../services/orderService';
import { useAuth } from '../contexts/AuthContext';
import { Order } from '../types/cart';

/** Chaavi tokens (chaavi_ratings_page.html) */
const R = {
  page: '#f5f2ec',
  ink: '#3a2e1e',
  muted: '#a89880',
  border: '#e8e0d4',
  rule: '#f5f0e8',
  accent: '#c0873a',
  accentDark: '#b07030',
  warmBrown: '#5a4a35',
  barMuted: '#7a6a55',
  chipBg: '#fdf0e0',
  chipText: '#b07030',
  footerBg: '#fdfaf5',
  starOn: '#c0873a',
  starOff: '#e8d5b0',
  dateMuted: '#c0b8a8',
};

const THUMB_BY_TYPE: Record<string, string> = {
  THEME: '🎨',
  INVENTORY: '🪑',
  PLATE: '🍽️',
  DISH: '🥘',
};

const TYPE_LABEL: Record<string, string> = {
  THEME: 'Theme',
  INVENTORY: 'Decor',
  PLATE: 'Plate',
  DISH: 'Dish',
};

type TabKey = 'all' | 'pending';

interface PendingItem {
  key: string;
  itemId: string;
  itemType: 'THEME' | 'INVENTORY' | 'PLATE' | 'DISH';
  itemName: string;
  businessId: string;
  businessName: string;
  orderId: string;
  completedLabel: string;
}

function itemKey(itemId: string, itemType: string): string {
  return `${itemId}-${itemType.toUpperCase()}`;
}

function findOrderItemForRating(rating: Rating, orders: Order[]) {
  for (const order of orders) {
    const hit = order.orderItems?.find(
      i => i.itemId === rating.itemId && i.itemType.toUpperCase() === rating.itemType
    );
    if (hit) return { order, item: hit };
  }
  return null;
}

function extractTags(comment: string): string[] {
  if (!comment?.trim()) return [];
  const parts = comment
    .split(/[.!?\n]+/)
    .map(s => s.trim())
    .filter(s => s.length > 2 && s.length < 80);
  const tags = parts.slice(0, 4).map(p => (p.length > 32 ? `${p.slice(0, 29)}…` : p));
  return tags.length ? tags : [comment.trim().slice(0, 40) + (comment.length > 40 ? '…' : '')];
}

const StarRow: React.FC<{ value: number; size?: number }> = ({ value, size = 15 }) => {
  const rounded = Math.round(value);
  return (
    <Box sx={{ display: 'flex', gap: '2px', justifyContent: 'flex-end' }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Box
          key={i}
          component="svg"
          viewBox="0 0 16 16"
          sx={{ width: size, height: size, flexShrink: 0 }}
        >
          <polygon
            points="8,1 10,6 15,6 11,9 13,14 8,11 3,14 5,9 1,6 6,6"
            fill={i <= rounded ? R.starOn : R.starOff}
          />
        </Box>
      ))}
    </Box>
  );
};

const InlineStars: React.FC<{ value: number }> = ({ value }) => {
  const filled = Math.min(5, Math.max(0, Math.round(value)));
  return (
    <Box sx={{ display: 'flex', gap: '2px', mt: 0.75 }}>
      {[1, 2, 3, 4, 5].map(i => (
        <Box key={i} component="svg" viewBox="0 0 16 16" sx={{ width: 14, height: 14 }}>
          <polygon
            points="8,1 10,6 15,6 11,9 13,14 8,11 3,14 5,9 1,6 6,6"
            fill={i <= filled ? R.starOn : R.starOff}
          />
        </Box>
      ))}
    </Box>
  );
};

const ClientRatings: React.FC = () => {
  const { user } = useAuth();
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('all');
  const [editingRating, setEditingRating] = useState<Rating | null>(null);
  const [ratingDraft, setRatingDraft] = useState<{
    itemId: string;
    itemType: 'THEME' | 'INVENTORY' | 'PLATE' | 'DISH';
    itemName: string;
    businessId: string;
    orderId?: string;
    existing?: Rating;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ratingToDelete, setRatingToDelete] = useState<Rating | null>(null);

  const loadAll = useCallback(async () => {
    if (!user?.phoneNumber) return;
    try {
      setLoading(true);
      setError(null);
      const [clientRatings, orderList] = await Promise.all([
        ratingService.getRatingsByClient(),
        orderService.getOrdersByUserId(user.phoneNumber),
      ]);
      const active = (clientRatings || []).filter(r => r.isActive !== false);
      setRatings(active.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      setOrders(Array.isArray(orderList) ? orderList : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ratings');
      setRatings([]);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user?.phoneNumber]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const ratedKeys = useMemo(
    () => new Set(ratings.map(r => itemKey(r.itemId, r.itemType))),
    [ratings]
  );

  const pendingItems = useMemo((): PendingItem[] => {
    const out: PendingItem[] = [];
    const seen = new Set<string>();
    for (const order of orders) {
      if (order.status !== 'DELIVERED' || !order.orderItems?.length) continue;
      for (const line of order.orderItems) {
        const key = itemKey(line.itemId, line.itemType);
        if (seen.has(key) || ratedKeys.has(key)) continue;
        seen.add(key);
        const type = line.itemType.toUpperCase() as PendingItem['itemType'];
        if (!['THEME', 'INVENTORY', 'PLATE', 'DISH'].includes(type)) continue;
        const d = line.bookingDate || order.deliveryDate || order.orderDate;
        let completedLabel: string;
        try {
          completedLabel = new Date(d).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        } catch {
          completedLabel = String(d);
        }
        out.push({
          key,
          itemId: line.itemId,
          itemType: type,
          itemName: line.itemName,
          businessId: line.businessId,
          businessName: line.businessName,
          orderId: String(order.orderId),
          completedLabel,
        });
      }
    }
    return out;
  }, [orders, ratedKeys]);

  const summary = useMemo(() => {
    const n = ratings.length;
    const avg = n ? ratings.reduce((s, r) => s + r.rating, 0) / n : 0;
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } as Record<number, number>;
    ratings.forEach(r => {
      const k = Math.min(5, Math.max(1, Math.round(r.rating))) as 1 | 2 | 3 | 4 | 5;
      breakdown[k]++;
    });
    const maxCount = Math.max(...Object.values(breakdown), 1);
    return { n, avg, breakdown, maxCount };
  }, [ratings]);

  const handleEditRating = (rating: Rating) => {
    setEditingRating(rating);
    setRatingDraft(null);
  };

  const openNewRating = (p: PendingItem) => {
    setEditingRating(null);
    setRatingDraft({
      itemId: p.itemId,
      itemType: p.itemType,
      itemName: p.itemName,
      businessId: p.businessId,
      orderId: p.orderId,
    });
  };

  const handleDeleteRating = (rating: Rating) => {
    setRatingToDelete(rating);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ratingToDelete) return;
    try {
      await ratingService.deleteRating(ratingToDelete.ratingId);
      setRatings(prev => prev.filter(r => r.ratingId !== ratingToDelete.ratingId));
      setDeleteDialogOpen(false);
      setRatingToDelete(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete rating');
    }
  };

  const handleRatingUpdated = (updated: Rating) => {
    setRatings(prev => {
      const idx = prev.findIndex(r => r.ratingId === updated.ratingId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = updated;
        return next.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      }
      return [updated, ...prev].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    });
    setEditingRating(null);
    setRatingDraft(null);
    void loadAll();
  };

  const formatReviewDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const tabBtn = (key: TabKey, label: string) => (
    <Box
      component="button"
      type="button"
      key={key}
      onClick={() => setTab(key)}
      sx={{
        py: 1.25,
        px: 2.25,
        border: 'none',
        background: 'none',
        fontSize: '0.8125rem',
        color: tab === key ? R.accent : R.muted,
        cursor: 'pointer',
        fontFamily: '"Jost", "Roboto", sans-serif',
        borderBottom: '2px solid',
        borderColor: tab === key ? R.accent : 'transparent',
        mb: '-0.5px',
        fontWeight: tab === key ? 500 : 400,
        '&:hover': { color: R.warmBrown },
      }}
    >
      {label}
    </Box>
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
        sx={{ bgcolor: R.page }}
      >
        <CircularProgress sx={{ color: R.accent }} />
      </Box>
    );
  }

  const dialogOpen = Boolean(editingRating || ratingDraft);

  return (
    <Box sx={{ bgcolor: R.page, minHeight: '100%', width: '100%', pb: 6 }}>
      <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: { xs: 2, sm: 3.5 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 3,
          }}
        >
          <Box>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '1.25rem', sm: '1.375rem' },
                fontWeight: 500,
                color: R.ink,
                fontFamily: '"Playfair Display", Georgia, serif',
              }}
            >
              My ratings &amp; reviews
            </Typography>
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: R.muted,
                fontFamily: '"Jost", "Roboto", sans-serif',
                mt: 0.5,
                maxWidth: 520,
              }}
            >
              Manage your feedback for vendors, themes, and services.
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={1.75} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: '12px',
                border: `0.5px solid ${R.border}`,
                p: 2.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: R.muted,
                  fontFamily: '"Jost", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  mb: 1,
                }}
              >
                Average rating given
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.625rem',
                  fontWeight: 500,
                  color: R.ink,
                  fontFamily: '"Jost", sans-serif',
                  lineHeight: 1,
                }}
              >
                {summary.n ? summary.avg.toFixed(1) : '—'}
              </Typography>
              {summary.n > 0 ? <InlineStars value={summary.avg} /> : null}
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: R.muted,
                  fontFamily: '"Jost", sans-serif',
                  mt: 0.75,
                }}
              >
                {summary.n === 0 ? 'No reviews yet' : `across ${summary.n} review${summary.n === 1 ? '' : 's'}`}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: '12px',
                border: `0.5px solid ${R.border}`,
                p: 2.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: R.muted,
                  fontFamily: '"Jost", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  mb: 1,
                }}
              >
                Rating breakdown
              </Typography>
              {[5, 4, 3, 2, 1].map(star => {
                const cnt = summary.breakdown[star as 1 | 2 | 3 | 4 | 5];
                const pct = summary.maxCount ? (cnt / summary.maxCount) * 100 : 0;
                return (
                  <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.625 }}>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: R.barMuted,
                        fontFamily: '"Jost", sans-serif',
                        width: 18,
                        textAlign: 'right',
                      }}
                    >
                      {star}
                    </Typography>
                    <Box
                      sx={{
                        flex: 1,
                        height: 6,
                        bgcolor: '#f0ebe0',
                        borderRadius: '3px',
                        overflow: 'hidden',
                      }}
                    >
                      <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: R.accent, borderRadius: '3px' }} />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        color: R.muted,
                        fontFamily: '"Jost", sans-serif',
                        width: 18,
                      }}
                    >
                      {cnt}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                bgcolor: '#fff',
                borderRadius: '12px',
                border: `0.5px solid ${R.border}`,
                p: 2.25,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  color: R.muted,
                  fontFamily: '"Jost", sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.07em',
                  mb: 1,
                }}
              >
                Pending reviews
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.625rem',
                  fontWeight: 500,
                  color: R.accent,
                  fontFamily: '"Jost", sans-serif',
                  lineHeight: 1,
                }}
              >
                {pendingItems.length}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: R.accentDark,
                  fontFamily: '"Jost", sans-serif',
                  mt: 0.75,
                }}
              >
                vendors awaiting your feedback
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', borderBottom: `0.5px solid ${R.border}`, mb: 2.5 }}>
          {tabBtn('all', 'All reviews')}
          {tabBtn('pending', `Pending${pendingItems.length ? ` (${pendingItems.length})` : ''}`)}
        </Box>

        {tab === 'all' && (
          <Box>
            {ratings.length === 0 ? (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '12px',
                  border: `0.5px solid ${R.border}`,
                  py: 6,
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: R.warmBrown, fontFamily: '"Jost", sans-serif', fontWeight: 500 }}>
                  No reviews yet
                </Typography>
                <Typography sx={{ color: R.muted, fontSize: '0.8125rem', mt: 1, fontFamily: '"Jost", sans-serif' }}>
                  Rate items from your delivered orders (see Pending) or place an order first.
                </Typography>
              </Box>
            ) : (
              ratings.map(rating => {
                const hit = findOrderItemForRating(rating, orders);
                const vendorName = hit?.item.businessName || 'Vendor';
                const itemName = hit?.item.itemName || `Item ${rating.itemId}`;
                const typeLbl = TYPE_LABEL[rating.itemType] || rating.itemType;
                const thumb = THUMB_BY_TYPE[rating.itemType] || '📦';
                const tags = extractTags(rating.comment);
                return (
                  <Box
                    key={rating.ratingId}
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      border: `0.5px solid ${R.border}`,
                      overflow: 'hidden',
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        pt: 2,
                        pb: 1.5,
                        borderBottom: `0.5px solid ${R.rule}`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: '10px',
                            bgcolor: '#f0ebe0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '1.25rem',
                            flexShrink: 0,
                          }}
                        >
                          {thumb}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              fontWeight: 500,
                              color: R.ink,
                              fontFamily: '"Jost", sans-serif',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {vendorName}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: R.muted,
                              fontFamily: '"Jost", sans-serif',
                              mt: 0.25,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {typeLbl} · {itemName}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
                        <Typography
                          sx={{
                            fontSize: '0.6875rem',
                            color: R.dateMuted,
                            fontFamily: '"Jost", sans-serif',
                            mb: 0.625,
                          }}
                        >
                          {formatReviewDate(rating.createdAt)}
                        </Typography>
                        <StarRow value={rating.rating} />
                      </Box>
                    </Box>
                    <Box sx={{ px: 2.5, py: 1.75 }}>
                      {rating.comment ? (
                        <Typography
                          sx={{
                            fontSize: '0.8125rem',
                            color: R.warmBrown,
                            fontFamily: '"Jost", sans-serif',
                            lineHeight: 1.6,
                          }}
                        >
                          {rating.comment}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: '0.8125rem', color: R.muted, fontStyle: 'italic' }}>
                          No written review
                        </Typography>
                      )}
                      {tags.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1.25 }}>
                          {tags.map(t => (
                            <Typography
                              key={t}
                              component="span"
                              sx={{
                                fontSize: '0.6875rem',
                                py: 0.375,
                                px: 1.125,
                                borderRadius: '10px',
                                bgcolor: R.chipBg,
                                color: R.chipText,
                                fontFamily: '"Jost", sans-serif',
                              }}
                            >
                              {t}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 1.25,
                        borderTop: `0.5px solid ${R.rule}`,
                        bgcolor: R.footerBg,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.75rem', color: R.muted, fontFamily: '"Jost", sans-serif' }}>
                        Thank you for helping other clients choose vendors.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Button
                          size="small"
                          onClick={() => handleDeleteRating(rating)}
                          sx={{
                            fontSize: '0.75rem',
                            color: R.muted,
                            textTransform: 'none',
                            minWidth: 0,
                            fontFamily: '"Jost", sans-serif',
                          }}
                        >
                          Remove
                        </Button>
                        <Button
                          size="small"
                          onClick={() => handleEditRating(rating)}
                          sx={{
                            fontSize: '0.75rem',
                            color: R.accent,
                            textTransform: 'none',
                            fontFamily: '"Jost", sans-serif',
                            borderRadius: '6px',
                            '&:hover': { bgcolor: R.chipBg },
                          }}
                        >
                          Edit review
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        )}

        {tab === 'pending' && (
          <Box>
            {pendingItems.length === 0 ? (
              <Box
                sx={{
                  bgcolor: '#fff',
                  borderRadius: '12px',
                  border: `0.5px solid ${R.border}`,
                  py: 6,
                  px: 2,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ color: R.warmBrown, fontFamily: '"Jost", sans-serif', fontWeight: 500 }}>
                  You&apos;re all caught up
                </Typography>
                <Typography sx={{ color: R.muted, fontSize: '0.8125rem', mt: 1, fontFamily: '"Jost", sans-serif' }}>
                  No delivered orders are waiting for a review.
                </Typography>
              </Box>
            ) : (
              pendingItems.map(p => {
                const thumb = THUMB_BY_TYPE[p.itemType] || '🎊';
                return (
                  <Box
                    key={p.key}
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: '12px',
                      border: `0.5px solid ${R.border}`,
                      p: 2,
                      mb: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.75,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '10px',
                        bgcolor: '#f0ebe0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.25rem',
                        flexShrink: 0,
                      }}
                    >
                      {thumb}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography
                        sx={{
                          fontSize: '0.8125rem',
                          fontWeight: 500,
                          color: R.ink,
                          fontFamily: '"Jost", sans-serif',
                        }}
                      >
                        {p.businessName} — {p.itemName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: R.muted,
                          fontFamily: '"Jost", sans-serif',
                          mt: 0.25,
                        }}
                      >
                        Event completed {p.completedLabel} · awaiting your review
                      </Typography>
                    </Box>
                    <Button
                      variant="contained"
                      disableElevation
                      onClick={() => openNewRating(p)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        py: 1,
                        px: 1.75,
                        borderRadius: '8px',
                        bgcolor: R.accent,
                        fontFamily: '"Jost", sans-serif',
                        whiteSpace: 'nowrap',
                        gap: 0.75,
                        '&:hover': { bgcolor: '#a87030' },
                      }}
                      startIcon={<StarMiniIcon sx={{ fontSize: 14, color: '#fff' }} />}
                    >
                      Rate now
                    </Button>
                  </Box>
                );
              })
            )}
          </Box>
        )}
      </Box>

      {dialogOpen && (editingRating || ratingDraft) && (
        <RatingComponent
          open
          onClose={() => {
            setEditingRating(null);
            setRatingDraft(null);
          }}
          itemId={(editingRating ?? ratingDraft)!.itemId}
          itemType={(editingRating ?? ratingDraft)!.itemType}
          itemName={
            (editingRating ? findOrderItemForRating(editingRating, orders)?.item.itemName : undefined) ||
            ratingDraft?.itemName ||
            'Item'
          }
          businessId={(editingRating ?? ratingDraft)!.businessId}
          orderId={editingRating?.orderId ?? ratingDraft?.orderId}
          existingRating={editingRating ?? undefined}
          onRatingSubmitted={handleRatingUpdated}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete review</DialogTitle>
        <DialogContent>
          <Typography>Remove this review? You can submit a new one later from Pending if the order stays delivered.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => void handleDeleteConfirm()} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientRatings;
